import noop from 'lodash/noop';
import { FC, PropsWithChildren } from 'react';
import { CollapsibleContext } from '../collapsible-context';

export type CollapsibleProviderProps = PropsWithChildren & {
  /**
   * Indicates whether a certain element, feature, or state is open or active.
   *
   * This variable is optional and is represented as a boolean.
   * - `true` typically means the element or feature is open/active.
   * - `false` typically means the element or feature is closed/inactive.
   */
  isOpen?: boolean;

  /**
   * A callback function that is triggered when a toggle action occurs.
   * This function is optional and can be provided to handle custom behavior
   * whenever a toggle state changes.
   */
  onToggle?: () => void;
};

/**
 * CollapsibleProvider is a functional component that provides context for managing collapsible state.
 * It accepts props for controlling the open state and a callback function for toggling the state
 * and exposes these values via the CollapsibleContext.
 */
export const CollapsibleProvider: FC<CollapsibleProviderProps> = ({ onToggle, isOpen, children }) => {
  return (
    <CollapsibleContext.Provider value={{ open: isOpen ?? false, toggle: onToggle ?? noop }}>
      {children}
    </CollapsibleContext.Provider>
  );
};
